import React, { useState } from "react";
import {
  CardFooter,
  Typography,
  Button,
} from "@material-tailwind/react";
import { Hearing } from "../utils/entities/hearing";
import { FaMap, FaMoneyBill, FaVideo, FaCalendar } from "react-icons/fa6";
import api from "../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/helpers";
import { formatHearingTime } from "../utils/timezoneDisplay";

interface HearingCardProps {
  hearing: Partial<Hearing>;
  removeInviteFromPending?: (hearingId: number) => void;
  borderColor?: string;
}


const HearingCard: React.FC<HearingCardProps> = ({
  hearing,
  removeInviteFromPending,
  borderColor = "border-l-transparent",
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();


  const renderModality = (modality: string) => {
    if (modality === "1") {
      return (
        <div className="flex items-center gap-2 text-gray-700">
          <FaMap className="text-gray-500" />
          <span>Presencial - {hearing.court} vara</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-gray-700">
        <FaVideo className="text-gray-500" />
        <span>Virtual</span>
      </div>
    );
  };

  const handleConfirmAction = async (action: any, text: string) => {
    const proceed = window.confirm(text);
    if (proceed) {
      await action();
    }
  };

  const respondInvite = async (id: number, accepted: boolean) => {
    try {
      setLoading(true);
      await api.post(`/hearings/${id}/invite`, {
        accepted,
      });
      setLoading(false);
      if (accepted) {
        navigate(`/hearings/${id}`);
      } else {
        removeInviteFromPending!(id);
      }
    } catch (error: any) {
      console.error(error);
      setLoading(false);
      
      // Verificar se é erro de conflito de horário
      if (error?.response?.status === 409) {
        window.alert(
          error?.response?.data?.message || 
          "Você já possui uma audiência aceita no mesmo horário. Não é possível aceitar audiências com conflito de horário."
        );
      } else {
        window.alert(
          "Ocorreu um erro ao responder o convite. Por favor, atualize a página e tente novamente. Caso o problema persista, entre em contato conosco via WhatsApp."
        );
      }
    }
  };

  const loadInvitationActions = (hearingId: number) => {
    return (
      <div className="grid grid-cols-3 gap-1.5 md:gap-2 w-full">
        <Button
          className="w-full !min-w-0 bg-green-600 hover:bg-green-700 text-white !rounded-md !shadow-none !normal-case !h-7 md:!h-10 !py-1 md:!py-2 !px-1.5 md:!px-3 text-[11px] md:text-sm !leading-none border-0"
          onClick={async () =>
            handleConfirmAction(
              async () => respondInvite(hearingId, true),
              "Confirma o aceite do convite?"
            )
          }
          disabled={loading}
        >
          Aceitar
        </Button>
        <Button
          className="w-full !min-w-0 bg-red-600 hover:bg-red-700 text-white !rounded-md !shadow-none !normal-case !h-7 md:!h-10 !py-1 md:!py-2 !px-1.5 md:!px-3 text-[11px] md:text-sm !leading-none border-0"
          onClick={async () =>
            handleConfirmAction(
              async () => respondInvite(hearingId, false),
              "Confirma a recusa do convite?"
            )
          }
          disabled={loading}
        >
          Recusar
        </Button>
        <Button
          variant="outlined"
          className="w-full !min-w-0 !rounded-md !shadow-none !normal-case !h-7 md:!h-10 !py-1 md:!py-2 !px-1.5 md:!px-3 text-[11px] md:text-sm !leading-none"
          onClick={() => navigate(`/hearings/${hearingId}`)}
        >
          Detalhes
        </Button>
      </div>
    );
  };

  const formatDate = (date: string) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const loadHearingActions = () => {
    const hearingId = hearing.id as number;
    if (hearing.agentStatus === 1) {
      return loadInvitationActions(hearingId);
    }
    if (hearing.agentStatus === 2) {
      if (new Date(hearing.hearingDate!) < new Date()) {
        return (
          <div className="w-full">
            <Button
              className="w-full !min-w-0 bg-green-600 hover:bg-green-700 text-white !rounded-md !shadow-none !normal-case !h-7 md:!h-10 !py-1 md:!py-2 !px-1.5 md:!px-3 text-[11px] md:text-sm !leading-none border-0"
              onClick={() => navigate(`/hearings/${hearingId}`)}
            >
              Enviar relatório
            </Button>
          </div>
        );
      } else {
        return (
          <div className="w-full">
            <Button
              variant="outlined"
              className="w-full !min-w-0 !rounded-md !shadow-none !normal-case !h-7 md:!h-10 !py-1 md:!py-2 !px-1.5 md:!px-3 text-[11px] md:text-sm !leading-none"
              onClick={() => navigate(`/hearings/${hearingId}`)}
            >
              Ver detalhes
            </Button>
          </div>
        );
      }
    }
  };

  return (
    <div
      className={`bg-white border border-gray-200 shadow-sm hover:shadow-md rounded-xl p-2 md:p-3 transition-shadow duration-200 mx-auto flex flex-col h-full border-l-4 ${borderColor}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[13px] md:text-base font-semibold text-preppoBlue">
          {hearing.hearingType.description} - {hearing.company.tradeName}
        </h3>
        <FaCalendar className="text-base md:text-lg text-gray-400" />
      </div>
      <div className="flex-grow">
        <p className="text-gray-600 mb-1 text-xs md:text-sm">Processo: {hearing.processNumber}</p>
        <p className="text-preppoBlue font-medium mb-2 md:mb-3 text-xs md:text-sm flex items-center">
          <span>{formatDate(hearing.hearingDate?.toString() || "")}</span>
          {hearing.hearingTime && (
            <>
              <span className="mx-2 text-gray-300">•</span>
              <span className="text-gray-700">{formatHearingTime(hearing as any)}</span>
            </>
          )}
        </p>

        <div className="flex items-center mb-1 text-xs md:text-sm text-gray-700">
          {renderModality(hearing.modality!)}
        </div>
        {hearing.hearingType?.id !== 6 && (
          <div className="flex items-center mb-2 md:mb-3 text-xs md:text-sm text-gray-700">
            <FaMoneyBill className="mr-2 text-gray-500" />
            {formatCurrency(hearing.agentFee)}
          </div>
        )}

        {hearing.agentStatus === 1 && (
          <Typography variant="small" className="text-gray-500 italic my-2 md:my-3 text-[11px] md:text-sm">
            Ao aceitar o convite, você declara ter lido e aceito os{" "}
            <a href="./tc-preppo.pdf" target="_blank" className="underline">
              termos da Preppo
            </a>
            .
          </Typography>
        )}
      </div>

      <CardFooter className="pt-0 px-0 flex items-end">
        {loadHearingActions() || <div className="w-full" />}
      </CardFooter>
    </div>
  );
};

export default HearingCard;
